import { FileText, Inbox, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { WorkspaceStage } from '@jobos/shared';

/**
 * "Nothing waiting to be billed" and "no invoices match these filters" are
 * different facts, and conflating them makes a filtered-to-nothing view look
 * like an empty business (spec §6). The stage supplies the first message; an
 * active filter set replaces it with the second.
 */

const STAGE_MESSAGES: Record<WorkspaceStage, { title: string; body: string }> = {
  to_bill: {
    title: 'Nothing waiting to be billed',
    body: 'Jobs appear here once their job card is ready and they still have value left to invoice.',
  },
  drafts: {
    title: 'Nothing waiting to be sent',
    body: 'Invoices you create sit here until you mark them as sent.',
  },
  sent: {
    title: 'Nothing outstanding',
    body: 'Every sent invoice has been paid in full.',
  },
  paid: {
    title: 'No paid invoices yet',
    body: 'Invoices move here once the payments against them cover the full amount.',
  },
};

export interface WorkspaceEmptyStateProps {
  stage: WorkspaceStage;
  hasFilters: boolean;
  onClearFilters: () => void;
}

export function WorkspaceEmptyState({ stage, hasFilters, onClearFilters }: WorkspaceEmptyStateProps) {
  if (hasFilters) {
    return (
      <div className="rounded-lg border bg-white p-12 text-center">
        <Search className="mx-auto mb-3 h-10 w-10 text-gray-300" />
        <p className="font-medium text-gray-700">Nothing matches these filters</p>
        <p className="mt-1 text-sm text-gray-500">
          There may still be work in this stage — the filters are hiding it.
        </p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onClearFilters}>
          Clear filters
        </Button>
      </div>
    );
  }

  const { title, body } = STAGE_MESSAGES[stage];
  const Icon = stage === 'to_bill' ? Inbox : FileText;

  return (
    <div className="rounded-lg border bg-white p-12 text-center">
      <Icon className="mx-auto mb-3 h-10 w-10 text-gray-300" />
      <p className="font-medium text-gray-700">{title}</p>
      <p className="mt-1 text-sm text-gray-500">{body}</p>
    </div>
  );
}
